import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

interface UpdateInvoiceParams {
  invoiceId: string;
  values: any;
  estimate: any;
  lineItems: any[];
  selectedLineItems: string[];
  lineItemPercentages: { [key: string]: number };
  existingInvoices: any[];
  estimateTotal: number; // Caller must pass the already-adjusted estimate total
}

export const useInvoiceUpdate = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isReadOnly } = useImpersonationGuard();

  return useMutation({
    mutationFn: async ({ 
      invoiceId, 
      values, 
      estimate, 
      lineItems, 
      selectedLineItems, 
      lineItemPercentages, 
      existingInvoices,
      estimateTotal 
    }: UpdateInvoiceParams) => {
      if (isReadOnly) throw new Error('Cannot update invoices in View Only mode');
      if (!invoiceId || !estimate || !lineItems) {
        throw new Error('Missing required data');
      }

      // Fetch company VAT registration status
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('is_vat_registered')
        .eq('id', estimate.company_id)
        .single();

      if (companyError) {
        console.error('Error fetching company VAT status:', companyError);
        throw companyError;
      }

      const isVatRegistered = companyData?.is_vat_registered || false;

      // Calculate remaining balance excluding current invoice
      const otherInvoicesTotal = (existingInvoices || [])
        .filter((inv: any) => inv.id !== invoiceId)
        .reduce((sum: number, inv: any) => sum + (inv.total_amount || 0), 0);
      const remainingBalance = estimateTotal - otherInvoicesTotal;

      // Calculate totals based on invoice type
      let lineItemsTotal = 0;
      let itemsToInvoice = lineItems;
      
      if (values.invoice_type === 'partial' && selectedLineItems?.length > 0) {
        itemsToInvoice = lineItems.filter((item: any) => 
          selectedLineItems.includes(item.id)
        );
      }

      // Calculate the base total for selected items
      const baseTotal = itemsToInvoice.reduce((sum: number, item: any) => {
        const itemCost = item.manual_override && item.manual_total_cost !== null
          ? item.manual_total_cost
          : item.total_cost_snapshot;
        
        // Apply line item percentage if it's a partial invoice
        if (values.invoice_type === 'partial' && lineItemPercentages?.[item.id]) {
          return sum + (itemCost * lineItemPercentages[item.id] / 100);
        }
        
        return sum + itemCost;
      }, 0);

      // Apply calculation based on invoice type
      if (values.invoice_type === 'percentage' && values.percentage_of_estimate) {
        lineItemsTotal = estimateTotal * (values.percentage_of_estimate / 100);
      } else if (values.invoice_type === 'custom_amount' && values.custom_amount) {
        lineItemsTotal = values.custom_amount;
      } else if (values.invoice_type === 'full') {
        lineItemsTotal = remainingBalance;
      } else {
        lineItemsTotal = baseTotal;
      }

      let subtotal = lineItemsTotal;
      let taxAmount = 0;
      let totalAmount = lineItemsTotal;

      // Only apply tax calculations if company is VAT registered AND tax percentage > 0
      if (isVatRegistered && estimate.tax_percentage > 0) {
        if (estimate.tax_inclusive) {
          taxAmount = (lineItemsTotal * estimate.tax_percentage) / (100 + estimate.tax_percentage);
          subtotal = lineItemsTotal - taxAmount;
          totalAmount = lineItemsTotal;
        } else {
          subtotal = lineItemsTotal;
          taxAmount = (lineItemsTotal * estimate.tax_percentage) / 100;
          totalAmount = lineItemsTotal + taxAmount;
        }
      }

      // Update invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .update({
          title: values.title,
          description: values.description,
          due_date: values.due_date 
            ? `${values.due_date.getFullYear()}-${String(values.due_date.getMonth() + 1).padStart(2, '0')}-${String(values.due_date.getDate()).padStart(2, '0')}` 
            : null,
          payment_terms: values.payment_terms,
          special_instructions: values.special_instructions,
          invoice_type: values.invoice_type || 'full',
          percentage_of_estimate: values.percentage_of_estimate,
          subtotal: subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
        })
        .eq('id', invoiceId)
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Delete existing invoice line items
      const { error: deleteError } = await supabase
        .from('invoice_line_items')
        .delete()
        .eq('invoice_id', invoiceId);

      if (deleteError) throw deleteError;

      // Create new invoice line items
      const invoiceLineItems = itemsToInvoice.map((item: any, index: number) => {
        const quantityToInvoice = item.quantity_in_base_units;
        const unitCostToInvoice = item.manual_override && item.manual_unit_cost !== null
          ? item.manual_unit_cost
          : item.unit_cost_snapshot;
        const totalCostToInvoice = item.manual_override && item.manual_total_cost !== null
          ? item.manual_total_cost
          : item.total_cost_snapshot;

        return {
          invoice_id: invoiceId,
          estimate_line_item_id: item.id,
          description: item.description,
          base_unit: item.base_unit,
          quantity_invoiced: quantityToInvoice,
          unit_cost: unitCostToInvoice,
          total_cost: totalCostToInvoice,
          sort_order: index,
          is_partial_invoice: values.invoice_type !== 'full',
        };
      });

      const { error: lineItemsError } = await supabase
        .from('invoice_line_items')
        .insert(invoiceLineItems);

      if (lineItemsError) throw lineItemsError;

      return invoice;
    },
    onSuccess: (invoice) => {
      toast.success('Invoice updated successfully');
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['estimateInvoices', invoice.estimate_id] });
      navigate(`/invoices/${invoice.id}`);
    },
    onError: (error) => {
      console.error('Error updating invoice:', error);
      toast.error('Failed to update invoice');
    },
  });
};
